import { fabric } from 'fabric';
import type { Resolution } from 'modules/fabric-editor';
import type { LogoData, LogoSlot, LogoVariant } from './types';

/** Slot ordering for the inspector's 3×3 mini-field (center is intentionally absent). */
export const LOGO_SLOTS: LogoSlot[] = [
  'top-left',
  'top-center',
  'top-right',
  'middle-left',
  'middle-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

export const DEFAULT_LOGO_SLOT: LogoSlot = 'bottom-right';
/** Default size multiplier (1 = 100% of the variant's nominal design size). */
export const DEFAULT_LOGO_SCALE = 1;

// Minimum size multiplier per variant — a floor, not a cap: authors may scale
// the logo arbitrarily large. Corners (square) enforce a higher floor (60%)
// than the wide edge pill (40%), which already spans more of the edge at a
// given multiplier.
const MIN_SCALE_BY_VARIANT: Record<LogoVariant, number> = {
  square: 0.6,
  wide: 0.4,
};

export const DEFAULT_LOGO_MARGIN = 32;
export const MIN_LOGO_MARGIN = 0;
export const MAX_LOGO_MARGIN = 200;

const SLOT_VARIANT: Record<LogoSlot, LogoVariant> = {
  'top-left': 'square',
  'top-right': 'square',
  'bottom-left': 'square',
  'bottom-right': 'square',
  'top-center': 'wide',
  'middle-left': 'wide',
  'middle-right': 'wide',
  'bottom-center': 'wide',
};

export function variantForSlot(slot: LogoSlot): LogoVariant {
  return SLOT_VARIANT[slot];
}

export function scaleBoundsForVariant(variant: LogoVariant): { min: number } {
  return { min: MIN_SCALE_BY_VARIANT[variant] };
}

export function clampLogoScale(scale: number, variant: LogoVariant): number {
  const { min } = scaleBoundsForVariant(variant);
  if (!Number.isFinite(scale)) return DEFAULT_LOGO_SCALE;
  return Math.max(min, scale);
}

export function clampLogoMargin(margin: number): number {
  if (!Number.isFinite(margin)) return DEFAULT_LOGO_MARGIN;
  return Math.max(MIN_LOGO_MARGIN, Math.min(MAX_LOGO_MARGIN, Math.round(margin)));
}

interface VariantSpec {
  width: number;
  height: number;
  fontSize: number;
  radius: number;
}

// Nominal (scale = 1) geometry per variant. Square hugs the corners; wide is a
// short pill for the edge midpoints (matches the reference).
const VARIANT_SPEC: Record<LogoVariant, VariantSpec> = {
  square: { width: 132, height: 132, fontSize: 26, radius: 18 },
  wide: { width: 300, height: 92, fontSize: 40, radius: 22 },
};

const BG_COLOR = '#151a2d';
const TEXT_COLOR = '#ffffff';

// Locked, inert flags — mirror the grid group so the logo can't be selected,
// moved, scaled, or deleted directly. Positioning is inspector-only (8 slots).
const LOCK_FLAGS = {
  selectable: false,
  evented: false,
  hoverCursor: 'default',
  lockMovementX: true,
  lockMovementY: true,
  lockRotation: true,
  lockScalingX: true,
  lockScalingY: true,
  lockUniScaling: true,
  hasControls: false,
  objectCaching: false,
  subTargetCheck: false,
} as const;

export interface Point {
  left: number;
  top: number;
}

/** Top-left corner for a slot, insetting `margin` px from the canvas edges. */
export function slotToPosition(
  slot: LogoSlot,
  width: number,
  height: number,
  margin: number,
  resolution: Resolution,
): Point {
  const W = Math.max(1, Math.round(resolution.width));
  const H = Math.max(1, Math.round(resolution.height));
  const isLeft = slot === 'top-left' || slot === 'middle-left' || slot === 'bottom-left';
  const isRight = slot === 'top-right' || slot === 'middle-right' || slot === 'bottom-right';
  const isTop = slot === 'top-left' || slot === 'top-center' || slot === 'top-right';
  const isBottom = slot === 'bottom-left' || slot === 'bottom-center' || slot === 'bottom-right';
  const left = isLeft ? margin : isRight ? W - width - margin : Math.round((W - width) / 2);
  const top = isTop ? margin : isBottom ? H - height - margin : Math.round((H - height) / 2);
  return { left, top };
}

export function createLogoGroup(
  slot: LogoSlot,
  scale: number,
  margin: number,
  resolution: Resolution,
): fabric.Group {
  const variant = variantForSlot(slot);
  const spec = VARIANT_SPEC[variant];
  const s = clampLogoScale(scale, variant);
  const m = clampLogoMargin(margin);
  const width = Math.round(spec.width * s);
  const height = Math.round(spec.height * s);
  const radius = Math.round(spec.radius * s);
  const fontSize = Math.round(spec.fontSize * s);

  const bg = new fabric.Rect({
    left: 0,
    top: 0,
    width,
    height,
    rx: radius,
    ry: radius,
    fill: BG_COLOR,
    strokeWidth: 0,
    selectable: false,
    evented: false,
    objectCaching: false,
  });

  // Text-stub brand mark. Swap this for a real ImGame image asset later without
  // touching the plugin/inspector/render wiring.
  const label = new fabric.Textbox('ImGame', {
    left: width / 2,
    top: height / 2,
    width,
    originX: 'center',
    originY: 'center',
    textAlign: 'center',
    fontFamily: 'Arial',
    fontWeight: 'bold',
    fontSize,
    fill: TEXT_COLOR,
    selectable: false,
    evented: false,
    objectCaching: false,
  });

  const group = new fabric.Group([bg, label], {
    originX: 'left',
    originY: 'top',
    ...LOCK_FLAGS,
  });
  const pos = slotToPosition(slot, width, height, m, resolution);
  group.set({ left: pos.left, top: pos.top });

  // Materialize every default into `data` so it is the single source of truth
  // and round-trips through toJSON/loadFromJSON and undo/redo.
  const data: LogoData = { kind: 'logo', pin: 'top', variant, position: slot, scale: s, margin: m, isSave: true };
  group.data = data;
  return group;
}

export function getLogoData(obj: fabric.Object | undefined | null): LogoData | undefined {
  const data = obj?.data as { kind?: unknown } | undefined;
  if (!data || data.kind !== 'logo') return undefined;
  return obj!.data as LogoData;
}

export function findLogo(canvas: fabric.Canvas): fabric.Group | undefined {
  return canvas.getObjects().find((o) => getLogoData(o) !== undefined) as fabric.Group | undefined;
}
